import {useLayoutEffect} from '@github-ui/use-layout-effect'
import type {AnchorPosition} from '@primer/behaviors'
import type React from 'react'
import {createContext, useCallback, useContext, useMemo, useRef, useState} from 'react'
import {useFilter} from './RootContext'

import {Strings} from '../constants/strings'
import type {FilterQuery} from '../filter-query'
import {useDelimiterAnchorPosition} from '../hooks/use-delimiter-anchor-position'
import {BlockType, FilterValueType, type ARIAFilterSuggestion, type FilterSuggestion} from '../types'
import {
  checkFilterQuerySync,
  getEscapedFilterValue,
  getFilterValueByIndex,
  isIndexedFilterBlock,
  isFilterBlock,
  getFilterBlockChunkByCaret,
  getProviderKey,
} from '../utils'
import {updateScreenReaderFeedback} from '../utils/accessibility'
import {useFilterQuery} from './FilterQueryContext'
import {isFeatureEnabled} from '@github-ui/feature-flags'

interface SuggestionsContext {
  activeSuggestion: number
  hideSuggestions: () => void
  isFetchingSuggestions: boolean
  position?: AnchorPosition
  suggestions: ARIAFilterSuggestion[]
  showSuggestions: () => void
  suggestionsListRef: React.RefObject<HTMLDivElement>
  suggestionSelected: (value: FilterSuggestion) => void
  suggestionsVisible?: boolean
  updateSuggestions: (query: FilterQuery, caret?: number, shouldShowSuggestions?: boolean) => Promise<void>
  suggestionsHeading?: string
  activeSuggestionRef: React.RefObject<HTMLLIElement>
  suggestionsContainerRef: React.RefObject<HTMLUListElement>
  setActiveSuggestion: React.Dispatch<React.SetStateAction<number>>
  suggestionListCurrentTop: number
  setSuggestionListCurrentTop: React.Dispatch<React.SetStateAction<number>>
}

export const SuggestionsContext = createContext<SuggestionsContext | undefined>(undefined)

export const useSuggestions = () => {
  const context = useContext(SuggestionsContext)
  if (!context) {
    throw new Error('useSuggestions must be used inside a SuggestionsContext')
  }

  return context
}

export const MAXIMUM_SUGGESTIONS_TO_SHOW = 25

interface SuggestionsContextProviderProps {
  caretRef: React.RefObject<HTMLSpanElement>
  children: React.ReactNode
  inputRef: React.RefObject<HTMLInputElement>
}

export const SuggestionsContextProvider = ({children, caretRef, inputRef}: SuggestionsContextProviderProps) => {
  const {config, inputContextRef} = useFilter()
  const {
    filterQuery,
    filterProviders,
    insertIntoQuery,
    rawFilterRef,
    replaceActiveBlockWithNoBlock,
    updateFilter,
    updateFromExternal,
  } = useFilterQuery()

  const activeSuggestionRef = useRef<HTMLLIElement>(null)
  const suggestionsContainerRef = useRef<HTMLUListElement>(null)
  const suggestionsListRef = useRef<HTMLDivElement>(null)
  const fetchingTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const {position, updatePosition} = useDelimiterAnchorPosition(
    {
      caretElementRef: caretRef,
      floatingElementRef: suggestionsListRef,
      inputElementRef: inputRef,
      side: 'outside-bottom',
      align: 'start',
      alignmentOffset: -16,
      allowOutOfBounds: false,
    },
    [suggestionsListRef.current, inputContextRef.current?.styledInputBlockCount],
  )

  const [suggestionListCurrentTop, setSuggestionListCurrentTop] = useState(0)
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false)
  const [suggestionsVisible, setSuggestionsVisible] = useState(false)
  const [suggestions, setSuggestions] = useState<ARIAFilterSuggestion[]>([])
  const [suggestionsHeading, setSuggestionsHeading] = useState<string>('')
  const [activeSuggestion, setActiveSuggestion] = useState<number>(-1)

  const showSuggestions = useCallback(() => {
    if (suggestionsVisible) return
    setSuggestionsVisible(true)
  }, [suggestionsVisible])
  const hideSuggestions = useCallback(() => {
    setSuggestionsVisible(false)
    setActiveSuggestion(-1)
  }, [])

  const prefetchingEnabled = isFeatureEnabled('filter_prefetch_suggestions')

  const updateSuggestions = useCallback(
    async (filter?: FilterQuery, caret?: number) => {
      let internalShouldShowSuggestions = true
      // If there is no filter input, don't show suggestions
      if (config.variant === 'button') return

      // Setting a timeout to show the spinner so it's not flashing
      if (fetchingTimeoutRef.current) clearTimeout(fetchingTimeoutRef.current)
      fetchingTimeoutRef.current = setTimeout(() => setIsFetchingSuggestions(true), 100)

      // Prioritize the filter passed in, otherwise use the current filter from state
      const query = filter ?? filterQuery
      const caretIndex = caret ?? inputRef.current?.selectionStart ?? -1

      let cachedSuggestions = [] as ARIAFilterSuggestion[]
      const activeBlock = query.activeBlock

      // Before fetching async, we will check the provider for any pre-fetched suggestions that match the current filter value
      if (prefetchingEnabled && activeBlock && isFilterBlock(activeBlock) && isIndexedFilterBlock(activeBlock)) {
        const [type] = getFilterBlockChunkByCaret(activeBlock, caretIndex)
        const rawValue = activeBlock.value.raw

        // Look for cached suggestions if the caret is positioned on the value, and only if it even has a value
        if (type === 'value' && rawValue) {
          const filterKey = getProviderKey(activeBlock.key.value)

          const provider =
            filterProviders[filterKey] ??
            (config.aliasMatching ? Object.values(filterProviders).find(p => p.aliases?.includes(filterKey)) : null)

          cachedSuggestions = provider?.findPrefetchedSuggestions?.(rawValue) ?? []

          if (cachedSuggestions.length > 0) {
            // Make sure the menu is visible to prevent it from flashing
            showSuggestions()

            updateScreenReaderFeedback(
              `${cachedSuggestions.length} ${cachedSuggestions.length === 1 ? 'suggestion' : 'suggestions'}`,
            )

            // Immediately put any cached suggestions into the suggestions list while we wait for
            // any additional suggestions to come async so the user sees immediate feedback.
            setSuggestions(cachedSuggestions)

            // Update the position of the suggestions list as it may have changed
            updatePosition()
          }
        }
      }

      // We only want to fetch async suggestions if there aren't enough cached suggestions to fill the list.
      // This should always be the case if prefetching is disabled, as the cachedSuggestions array is empty.
      if (cachedSuggestions.length < MAXIMUM_SUGGESTIONS_TO_SHOW) {
        let aggregatedSuggestions = await query.getSuggestions(caretIndex, filterProviders, config)

        if (prefetchingEnabled) {
          // Keep the cached suggestions at the front of the list to prevent the results from shifting away from the user.
          aggregatedSuggestions.unshift(...cachedSuggestions)

          // Remove any duplicates
          const seen = new Set()
          aggregatedSuggestions = aggregatedSuggestions.filter(suggestion => {
            const key = suggestion.value
            return seen.has(key) ? false : seen.add(key)
          })

          // Limit the number of suggestions to show
          aggregatedSuggestions = aggregatedSuggestions.slice(0, MAXIMUM_SUGGESTIONS_TO_SHOW)
        }

        let suggestionsGroupHeading = ''

        if (activeBlock?.type === BlockType.Text && activeBlock.raw.startsWith('-')) {
          suggestionsGroupHeading = Strings.exclude
        }

        if (activeBlock && isIndexedFilterBlock(activeBlock)) {
          // Hide suggestion if the only suggestion is the currently entered filter key
          const indexedValue = getFilterValueByIndex(activeBlock.value.values, caretIndex)
          if (
            aggregatedSuggestions.length === 1 &&
            aggregatedSuggestions[0]?.value === indexedValue &&
            indexedValue.length > 0
          ) {
            internalShouldShowSuggestions = false
          }
        }

        if (checkFilterQuerySync(query, rawFilterRef?.current) && query.isValidated) {
          // Final check to make sure the query hasn't changed while we were fetching suggestions and that is validated
          updateScreenReaderFeedback(
            `${aggregatedSuggestions.length} ${aggregatedSuggestions.length === 1 ? 'suggestion' : 'suggestions'}`,
          )
          setSuggestions(aggregatedSuggestions)
          setActiveSuggestion(-1)
          setSuggestionsHeading(suggestionsGroupHeading)
          // Update the position of the suggestions menu
          updatePosition()
        }

        if (aggregatedSuggestions.length > 0 && internalShouldShowSuggestions && inputContextRef.current?.inputHasFocus)
          showSuggestions()
        else hideSuggestions()
      }

      // Clear any remaining fetching timeout
      clearTimeout(fetchingTimeoutRef.current)
      setTimeout(() => {
        setIsFetchingSuggestions(false)
      }, 1000)
    },
    [
      config,
      filterQuery,
      inputRef,
      prefetchingEnabled,
      filterProviders,
      showSuggestions,
      updatePosition,
      rawFilterRef,
      inputContextRef,
      hideSuggestions,
    ],
  )

  const suggestionSelected = useCallback(
    (suggestion: FilterSuggestion) => {
      setActiveSuggestion(-1)
      const currentBlock = filterQuery.activeBlock
      const isExcludeKeySuggestion = suggestion.value.toString().startsWith('-')

      let suggestedValue
      let shouldSubmit = false

      // Selected Filter Key
      if (!currentBlock || currentBlock.type !== BlockType.Filter) {
        suggestedValue = getEscapedFilterValue(suggestion.value)

        // only add the colon if the suggestion value is NOT the exclude key
        if (suggestion.value !== '-') {
          suggestedValue += ':'
        }
        // Selected Filter Value
      } else if (isExcludeKeySuggestion) {
        suggestedValue = getEscapedFilterValue(suggestion.value)
      } else {
        suggestedValue = getEscapedFilterValue(suggestion.value)
        shouldSubmit = true
        hideSuggestions()
      }

      const caretIndex = inputRef.current?.selectionStart ?? -1

      if (suggestion.type === FilterValueType.NoValue) {
        replaceActiveBlockWithNoBlock()
      } else if (suggestedValue) {
        insertIntoQuery(suggestedValue, caretIndex, shouldSubmit)
      } else {
        updateFilter()
      }
    },
    [filterQuery.activeBlock, hideSuggestions, inputRef, insertIntoQuery, replaceActiveBlockWithNoBlock, updateFilter],
  )

  useLayoutEffect(() => {
    updateFromExternal(async query => {
      if (checkFilterQuerySync(query, rawFilterRef?.current)) {
        await updateSuggestions(query, inputRef.current?.selectionStart ?? -1)
      }
    })
  }, [rawFilterRef, updateSuggestions, filterQuery.raw, filterQuery, updateFromExternal, inputRef])

  const suggestionsContextValue = useMemo(
    () => ({
      activeSuggestion,
      hideSuggestions,
      isFetchingSuggestions,
      position,
      showSuggestions,
      suggestionsHeading,
      suggestions,
      suggestionsListRef,
      suggestionSelected,
      suggestionsVisible,
      setActiveSuggestion,
      suggestionListCurrentTop,
      setSuggestionListCurrentTop,
      updateSuggestions,
      activeSuggestionRef,
      suggestionsContainerRef,
    }),
    [
      activeSuggestion,
      hideSuggestions,
      isFetchingSuggestions,
      position,
      showSuggestions,
      suggestionsHeading,
      suggestions,
      suggestionSelected,
      suggestionsVisible,
      suggestionListCurrentTop,
      updateSuggestions,
    ],
  )

  return <SuggestionsContext.Provider value={suggestionsContextValue}>{children}</SuggestionsContext.Provider>
}

try{ SuggestionsContext.displayName ||= 'SuggestionsContext' } catch {}
try{ SuggestionsContextProvider.displayName ||= 'SuggestionsContextProvider' } catch {}